import React, { useEffect, useRef, useState } from "react";

const resolveApiBase = () => {
  const envBase = process.env.REACT_APP_API_BASE_URL?.trim();

  if (typeof window !== "undefined") {
    const origin = window.location.origin.replace(/\/$/, "");

    if (envBase) {
      const sanitized = envBase.replace(/\/$/, "");

      if (
        sanitized !== origin ||
        !window.location.hostname.endsWith("teamcoin.de")
      ) {
        return sanitized;
      }

      return `${window.location.protocol}//api.teamcoin.de`;
    }

    if (window.location.hostname.endsWith("teamcoin.de")) {
      return `${window.location.protocol}//api.teamcoin.de`;
    }

    return origin;
  }

  return envBase || "http://localhost:5000";
};

const API_BASE = resolveApiBase();
console.log("API_BASE =", API_BASE);

const ChatWidget = () => {
  const [theme, setTheme] = useState(
    localStorage.getItem("chatTheme") ||
      (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light")
  );
  const [messages, setMessages] = useState([]);
  const [pending, setPending] = useState(false);
  const [showScrollBtn, setShowScrollBtn] = useState(false);

  const inputRef = useRef(null);
  const chatBoxRef = useRef(null);

  // Apply theme
  useEffect(() => {
    document.body.classList.remove("light", "dark");
    document.body.classList.add(theme);
    localStorage.setItem("chatTheme", theme);
  }, [theme]);

  // Welcome
  const didShowWelcome = useRef(false);

  useEffect(() => {
    if (!didShowWelcome.current) {
      pushBot("👋 Hi, I’m your <b>TeamCoin Assistant</b>.<br/>Ask me anything about TeamCoin & Sponsoring!");
      didShowWelcome.current = true;
    }
  }, []);

  const pushUser = (html) => setMessages((prev) => [...prev, { who: "user", html }]);
  const pushBot = (html) => setMessages((prev) => [...prev, { who: "bot", html }]);

  const scrollToBottom = (smooth = true) => {
    requestAnimationFrame(() => {
      chatBoxRef.current?.scrollTo({
        top: chatBoxRef.current.scrollHeight,
        behavior: smooth ? "smooth" : "auto",
      });
    });
  };

  const sendMessage = async () => {
    const text = inputRef.current.value.trim();
    if (!text) return;

    pushUser(text);
    inputRef.current.value = "";
    setPending(true);
    scrollToBottom();

    try {
      const res = await fetch(`${API_BASE}/api/chat/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text }),
      });
      const data = await res.json();

      if (data.faq_answer) {
        pushBot(data.faq_answer);
      } else if (data.error) {
        pushBot("❌ " + data.error);
      } else {
        pushBot("🤖 " + (data.results?.length ? JSON.stringify(data.results) : "No results found."));
      }
    } catch (err) {
      console.error("Chat error:", err);
      pushBot("❌ Error processing your request.");
    } finally {
      setPending(false);
      if (inputRef.current) {
        inputRef.current.focus(); // keep cursor ready
      }
      scrollToBottom();
    }
  };

  const onKeyDown = (e) => e.key === "Enter" && sendMessage();

  // Track scroll position
  const handleScroll = () => {
    if (!chatBoxRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = chatBoxRef.current;
    const nearBottom = scrollHeight - scrollTop - clientHeight < 50;
    setShowScrollBtn(!nearBottom);
  };

  return (
    <div style={{ maxWidth: 600, margin: "0 auto", fontFamily: "Segoe UI, sans-serif" }}>
      {/* Header */}
      <div
        style={{
          position: "relative",
          textAlign: "center",
          background: "linear-gradient(90deg,#007bff,#00b4d8)",
          padding: 15,
          borderRadius: "12px 12px 0 0",
          color: "#fff",
        }}
      >
        <img src="/logo.png" alt="TeamCoin" style={{ height: 45, marginBottom: 8 }} />
        <h2 style={{ margin: 0, fontSize: "1.2em" }}>TeamCoin Assistant</h2>
        <p style={{ margin: 0, fontSize: "0.85em", color: "#ffe082" }}>
          Your Sponsoring & Token Guide
        </p>

        <button
          onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            background: "rgba(255,255,255,0.2)",
            border: "none",
            borderRadius: "50%",
            width: 32,
            height: 32,
            color: "#fff",
            fontSize: 16,
            cursor: "pointer",
          }}
        >
          {theme === "dark" ? "🌞" : "🌙"}
        </button>
      </div>

      {/* Chat container */}
      <div
        style={{
          background: theme === "dark" ? "#1e1e1e" : "#fff",
          borderRadius: "0 0 12px 12px",
          boxShadow:
            theme === "dark" ? "0 6px 16px rgba(0,0,0,0.6)" : "0 6px 16px rgba(0,0,0,0.1)",
          padding: 15,
          position: "relative",
        }}
      >
        <div
          ref={chatBoxRef}
          onScroll={handleScroll}
          style={{ marginTop: 10, maxHeight: 400, overflowY: "auto", paddingRight: 5 }}
        >
          {messages.map((m, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                justifyContent: m.who === "user" ? "flex-end" : "flex-start",
                margin: "6px 0",
              }}
            >
              <div
                style={{
                  maxWidth: "75%",
                  background:
                    m.who === "user"
                      ? "#007bff"
                      : theme === "dark"
                      ? "#2c2c2c"
                      : "#f1f1f1",
                  color: m.who === "user" ? "#fff" : theme === "dark" ? "#eee" : "#333",
                  padding: "10px 14px",
                  borderRadius: 18,
                  fontSize: "0.9em",
                  lineHeight: 1.4,
                }}
                dangerouslySetInnerHTML={{ __html: m.html }}
              />
            </div>
          ))}
          {pending && (
            <div style={{ display: "flex", justifyContent: "flex-start" }}>
              <div className="typing-indicator" style={{ display: "flex", gap: 4, margin: "5px 0" }}>
                <span style={dotStyle}></span>
                <span style={{ ...dotStyle, animationDelay: "0.2s" }}></span>
                <span style={{ ...dotStyle, animationDelay: "0.4s" }}></span>
              </div>
            </div>
          )}
        </div>

        {/* Scroll-to-latest button (with fade) */}
        <button
          onClick={() => scrollToBottom(true)}
          style={{
            position: "absolute",
            bottom: 60,
            right: 20,
            background: "#007bff",
            border: "none",
            color: "#fff",
            borderRadius: "50%",
            width: 36,
            height: 36,
            fontSize: 18,
            cursor: "pointer",
            boxShadow: "0 3px 8px rgba(0,0,0,0.2)",
            transition: "opacity 0.3s",
            opacity: showScrollBtn ? 1 : 0,
            pointerEvents: showScrollBtn ? "auto" : "none",
          }}
        >
          ⬇️
        </button>

        {/* Input */}
        <div style={{ marginTop: 10, display: "flex" }}>
          <input
            ref={inputRef}
            type="text"
            placeholder="Type your message..."
            onKeyDown={onKeyDown}
            style={{
              flex: 1,
              padding: "12px 15px",
              border: "1px solid #ddd",
              borderRadius: 25,
              outline: "none",
              fontSize: "0.9em",
              background: theme === "dark" ? "#2c2c2c" : "#fff",
              color: theme === "dark" ? "#f1f1f1" : "#333",
            }}
          />
          <button
            onClick={sendMessage}
            disabled={pending}
            style={{
              marginLeft: 8,
              background: "#007bff",
              color: "#fff",
              border: "none",
              width: 44,
              height: 44,
              borderRadius: "50%",
              cursor: "pointer",
              fontSize: "1.1em",
              boxShadow: "0 3px 6px rgba(0,0,0,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "0.2s",
              opacity: pending ? 0.6 : 1,
            }}
          >
            ➤
          </button>
        </div>
      </div>
    </div>
  );
};

const dotStyle = {
  width: 6,
  height: 6,
  background: "#999",
  borderRadius: "50%",
  display: "inline-block",
  animation: "bounce 1.4s infinite",
};

export default ChatWidget;
